import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HotelsShopActions, HotelsShopSelectors } from "@b2bportal/core-hotels";
import type { RoomProduct } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";

export const useHotelRoomSelection = ({
  goToNextStep,
}: {
  goToNextStep: () => void;
}) => {
  const { t } = useI18nContext();
  const {
    data: rooms,
    isLoading,
    isError,
  } = useSelector(HotelsShopSelectors.getRooms);

  const dispatch = useDispatch();

  const nightCount = useSelector(HotelsShopSelectors.getTotalNights);

  const sortedRooms = useMemo(() => {
    if (!rooms) return rooms;

    // Sort rooms by total price (cheapest first)
    return [...rooms].sort((a, b) => {
      const priceA = a.products?.[0]?.tripTotal?.fiat?.value ?? 0;
      const priceB = b.products?.[0]?.tripTotal?.fiat?.value ?? 0;
      return priceA - priceB;
    });
  }, [rooms]);

  return useMemo(() => {
    return {
      context: {
        rooms: sortedRooms,
        isLoading,
        isError,
        nightCount: nightCount ?? 1,
      },
      handlers: {
        onRoomSelection: (product: RoomProduct) => {
          dispatch(HotelsShopActions.setSelectedRoomProduct(product));
          goToNextStep();
        },
      },
      text: {
        title: t("core-hotels.roomSelection.title"),
        noAvailableRooms: {
          title: t("core-hotels.roomSelection.noAvailableRooms.title"),
          subtitle: t("core-hotels.roomSelection.noAvailableRooms.subtitle"),
        },
      },
    };
  }, [dispatch, goToNextStep, isError, isLoading, nightCount, sortedRooms, t]);
};
